import { c as _c } from "react/compiler-runtime";
import { getErrorMessage } from '@onefootprint/request';
import type { SameTenantDupe } from '@onefootprint/request-types/dashboard';
import { LinkButton, Table } from '@onefootprint/ui';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isForbiddenError } from '@/components/forbidden';
import useCurrentEntityDuplicateData from '@/entity/hooks/use-current-entity-duplicate-data';
import useMobileLayout from '@/hooks/use-mobile-layout';
import useSession from '@/hooks/use-session';
import MobileRow from './components/mobile-row';
import Row from './components/row';
const COLLAPSED_ROW_COUNT = 10;
// Collapsing only pays off when it hides a few rows.
const MIN_HIDDEN_ROW_COUNT = 3;
type DuplicateDataProps = {
  /** Shows the duplicates as they were at this time instead of now. */
  timestamp?: string;
};
const DuplicateData = t0 => {
  const $ = _c(39);
  const {
    timestamp
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "duplicateData"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const router = useRouter();
  const {
    data: t2
  } = useSession();
  const {
    org
  } = t2;
  const {
    data: duplicateData,
    isPending,
    error
  } = useCurrentEntityDuplicateData(timestamp);
  const isMobile = useMobileLayout();
  const [isExpanded, setIsExpanded] = useState(false);
  let t3;
  if ($[1] !== duplicateData?.data) {
    t3 = duplicateData?.data ?? [];
    $[1] = duplicateData?.data;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const dupes = t3;
  const isCollapsible = dupes.length - COLLAPSED_ROW_COUNT >= MIN_HIDDEN_ROW_COUNT;
  let t4;
  if ($[3] !== error || $[4] !== org?.name || $[5] !== t) {
    t4 = () => {
      if (isForbiddenError(error)) {
        return t(_temp);
      }
      if (error) {
        return getErrorMessage(error);
      }
      return t(_temp2, {
        orgName: org?.name ?? ""
      });
    };
    $[3] = error;
    $[4] = org?.name;
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const getEmptyStateText = t4;
  let t5;
  if ($[7] !== isMobile || $[8] !== t) {
    t5 = isMobile ? [{
      text: t(_temp3)
    }] : [{
      text: t(_temp4),
      width: "12%"
    }, {
      text: t(_temp5),
      width: "25%"
    }, {
      text: t(_temp6),
      width: "33%"
    }, {
      text: t(_temp7),
      width: "13%"
    }, {
      text: t(_temp8),
      width: "17%"
    }];
    $[7] = isMobile;
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const columns = t5;
  let t6;
  if ($[10] !== router) {
    t6 = duplicateDataItem => {
      const fpId = duplicateDataItem?.fpId;
      if (!fpId) {
        return;
      }
      const [, base] = router.asPath.split("/");
      window.open(`/${base}/${fpId}`, "_blank", "noopener,noreferrer");
    };
    $[10] = router;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleRowClick = t6;
  let t7;
  if ($[12] !== t) {
    t7 = t(_temp9);
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== getEmptyStateText) {
    t8 = getEmptyStateText();
    $[14] = getEmptyStateText;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== dupes || $[17] !== isCollapsible || $[18] !== isExpanded) {
    t9 = isExpanded || !isCollapsible ? dupes : dupes.slice(0, COLLAPSED_ROW_COUNT);
    $[16] = dupes;
    $[17] = isCollapsible;
    $[18] = isExpanded;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== isMobile) {
    t10 = t11 => {
      const {
        item
      } = t11;
      return isMobile ? <MobileRow duplicateDataItem={item} /> : <Row duplicateDataItem={item} />;
    };
    $[20] = isMobile;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== columns || $[23] !== handleRowClick || $[24] !== isMobile || $[25] !== isPending || $[26] !== t10 || $[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
    t11 = <Table aria-label={t7} columns={columns} emptyStateText={t8} getKeyForRow={_temp10} hideThead={isMobile} isLoading={isPending} items={t9} onRowClick={handleRowClick} renderTr={t10} />;
    $[22] = columns;
    $[23] = handleRowClick;
    $[24] = isMobile;
    $[25] = isPending;
    $[26] = t10;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== dupes.length || $[32] !== isCollapsible || $[33] !== isExpanded || $[34] !== t) {
    t12 = isCollapsible && <LinkButton className="self-start mb-3 ml-5 max-md:ml-3" onClick={() => setIsExpanded(!isExpanded)}>{isExpanded ? t(_temp11) : t(_temp12, {
        count: dupes.length
      })}</LinkButton>;
    $[31] = dupes.length;
    $[32] = isCollapsible;
    $[33] = isExpanded;
    $[34] = t;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t11 || $[37] !== t12) {
    t13 = <div className="flex flex-col gap-3">{t11}{t12}</div>;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  return t13;
};
export default DuplicateData;
function _temp($) {
  return $.table.noPermission;
}
function _temp2($_0) {
  return $_0.table.emptyState;
}
function _temp3($_1) {
  return $_1.table.header.name;
}
function _temp4($_2) {
  return $_2.table.header.name;
}
function _temp5($_3) {
  return $_3.table.header.fpId;
}
function _temp6($_4) {
  return $_4.table.header.exactMatch;
}
function _temp7($_5) {
  return $_5.table.header.status;
}
function _temp8($_6) {
  return $_6.table.header.createdAt;
}
function _temp9($_7) {
  return $_7.table.ariaLabel;
}
function _temp10(duplicateDataItem_0) {
  return duplicateDataItem_0.fpId;
}
function _temp11($_8) {
  return $_8.showLess;
}
function _temp12($_9) {
  return $_9.showAll;
}